import { useId, useState } from 'react'
import { ChevronDown, ChevronUp, GripVertical } from 'lucide-react'

import { Button } from '@/ui/button'
import { LoadingNotice } from '@/ui/loading-notice'
import { RetryNotice } from '@/ui/retry-notice'
import { Frame, FrameCard } from '@/ui/frame'
import { useAnnounce } from '@/shared/providers/announcer-provider'
import { CounterKindPicker } from '../../lib/print-settings/counter-kind-picker'
import { usePrinterCounterKinds } from '../../lib/print-settings/printer-counter-kinds-queries'
import { usePrinterFormats } from '../../lib/print-settings/printer-formats-queries'
import { formatLabel } from '../../lib/print-settings/print-settings'
import type { PreferredCounters } from '../../api/preferred-counter-policy-api'

export function PreferredCounterFields({ entityId, value, onChange, disabled }: {
  entityId: string
  value: PreferredCounters
  onChange: (next: PreferredCounters) => void
  disabled: boolean
}) {
  const id = useId()
  const formats = usePrinterFormats(entityId)
  const counters = usePrinterCounterKinds(entityId)
  const [dragged, setDragged] = useState<string | null>(null)
  const announce = useAnnounce()

  function move(format: string, destination: number) {
    if (disabled || destination < 0 || destination >= value.formatPriority.length) return
    const next = value.formatPriority.filter((item) => item !== format)
    next.splice(destination, 0, format)
    onChange({ ...value, formatPriority: next })
    announce(`${formatLabel(format, formats.data ?? [])} is now ${destination + 1} of ${next.length}`)
  }

  return (
    <Frame title="Preferred counters" description="Copied into each printer’s settings once, when it first appears. Later policy changes do not overwrite its settings.">
      <FrameCard className="flex flex-col gap-6 p-4">
        <section className="flex flex-col gap-2" aria-labelledby={`${id}-priority`}>
          <h3 id={`${id}-priority`} className="text-sm font-medium">Format priority</h3>
          <p id={`${id}-priority-help`} className="text-muted-foreground text-xs">The first available format supplies all selected counters. Drag to reorder, or focus a handle and use the arrow keys.</p>
          {formats.isLoading && <LoadingNotice message="Loading format labels…" />}
          {formats.isError && <RetryNotice message="Couldn’t load format labels." onRetry={() => void formats.refetch()} />}
          <ol aria-labelledby={`${id}-priority`} className="divide-border border-border divide-y rounded-md border">
            {value.formatPriority.map((format, index) => {
              const label = formatLabel(format, formats.data ?? [])
              return (
                <li key={format} className="flex items-center gap-2 px-3 py-2"
                  onDragOver={(event) => { if (!disabled && dragged) event.preventDefault() }}
                  onDrop={(event) => { event.preventDefault(); if (dragged) move(dragged, index); setDragged(null) }}>
                  <button type="button" draggable={!disabled} disabled={disabled}
                    aria-label={`Reorder ${label}`} aria-describedby={`${id}-priority-help`}
                    className="text-muted-foreground cursor-grab rounded p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onDragStart={() => setDragged(format)} onDragEnd={() => setDragged(null)}
                    onKeyDown={(event) => {
                      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
                      event.preventDefault()
                      move(format, index + (event.key === 'ArrowUp' ? -1 : 1))
                    }}>
                    <GripVertical aria-hidden className="size-4" />
                  </button>
                  <span className="text-muted-foreground w-4 text-xs tabular-nums">{index + 1}</span>
                  <span className="flex-1 text-sm">{label}</span>
                  <Button type="button" variant="ghost" size="icon-sm" aria-label={`Move ${label} up`} disabled={disabled || index === 0} onClick={() => move(format, index - 1)}><ChevronUp aria-hidden /></Button>
                  <Button type="button" variant="ghost" size="icon-sm" aria-label={`Move ${label} down`} disabled={disabled || index === value.formatPriority.length - 1} onClick={() => move(format, index + 1)}><ChevronDown aria-hidden /></Button>
                </li>
              )
            })}
          </ol>
        </section>
        <section className="flex flex-col gap-2 [&_fieldset]:grid [&_fieldset]:grid-cols-2 [&_fieldset]:gap-x-4" aria-labelledby={`${id}-counters`}>
          <h3 id={`${id}-counters`} className="text-sm font-medium">Counters to display</h3>
          <CounterKindPicker value={value.countersToDisplay} options={counters.data ?? []}
            labelledBy={`${id}-counters`}
            describedBy={value.countersToDisplay.length === 0 ? `${id}-counter-error` : undefined}
            disabled={disabled}
            optionsState={counters.isLoading ? 'loading' : counters.isError ? 'error' : 'ready'}
            onRetry={() => void counters.refetch()}
            onChange={(countersToDisplay) => onChange({ ...value, countersToDisplay })} />
          {value.countersToDisplay.length === 0 && (
            <p id={`${id}-counter-error`} role="alert" className="text-destructive text-xs">
              Choose at least one counter.
            </p>
          )}
        </section>
      </FrameCard>
    </Frame>
  )
}
